/**
 * Moderation utilities for Oracle chat systems.
 * Shared between OraclePage (Sage on the Stage) and OracleSideGuide (Guide on the Side).
 */

export type ModerationSeverity =
  | 'CRITICAL_DISTRESS'
  | 'CRITICAL_LEGAL'
  | 'CRITICAL_THREAT'
  | 'HIGH'
  | 'MEDIUM'
  | 'UNKNOWN'

export interface ModerationConfig {
  severity: ModerationSeverity
  showCrisisResources: boolean
  i18nKey: string
}

export interface ModerationError {
  categories: string[]
  config: ModerationConfig
}

interface CategoryConfig {
  severity: ModerationSeverity
  showCrisisResources: boolean
}

/**
 * Category to severity mapping.
 * Based on OpenAI moderation categories but designed to be provider-agnostic.
 */
const CATEGORY_SEVERITY_MAP: Record<string, CategoryConfig> = {
  // Critical - Distress (self-harm with intent/instructions)
  'self-harm/intent': { severity: 'CRITICAL_DISTRESS', showCrisisResources: true },
  'self-harm/instructions': { severity: 'CRITICAL_DISTRESS', showCrisisResources: true },

  // Critical - Legal (sexual content involving minors)
  'sexual/minors': { severity: 'CRITICAL_LEGAL', showCrisisResources: false },

  // Critical - Threat (threatening content)
  'hate/threatening': { severity: 'CRITICAL_THREAT', showCrisisResources: false },
  'harassment/threatening': { severity: 'CRITICAL_THREAT', showCrisisResources: false },

  // High severity
  'violence/graphic': { severity: 'HIGH', showCrisisResources: false },
  'self-harm': { severity: 'HIGH', showCrisisResources: true },

  // Medium severity
  hate: { severity: 'MEDIUM', showCrisisResources: false },
  harassment: { severity: 'MEDIUM', showCrisisResources: false },
  sexual: { severity: 'MEDIUM', showCrisisResources: false },
  violence: { severity: 'MEDIUM', showCrisisResources: false },
}

/**
 * Severity priority order (higher index = higher priority).
 * When multiple categories are flagged, use the highest severity.
 */
const SEVERITY_PRIORITY: ModerationSeverity[] = [
  'MEDIUM',
  'HIGH',
  'CRITICAL_THREAT',
  'CRITICAL_LEGAL',
  'CRITICAL_DISTRESS',
]

/**
 * Known moderation error codes from the backend.
 * Extensible for other providers (Anthropic, etc.).
 */
const MODERATION_ERROR_CODES = new Set([
  'CONTENT_MODERATION', // OpenAI moderation
  // Future: add codes for other providers
])

/**
 * Parse moderation categories from the backend error message.
 * Expected format: "Content flagged for: category1, category2, ..."
 *
 * @param message - The error message from the backend
 * @returns Array of category strings
 */
export const parseModerationCategories = (message: string): string[] => {
  const prefix = 'Content flagged for:'
  const prefixIndex = message.indexOf(prefix)

  if (prefixIndex === -1) {
    return []
  }

  const categoriesStr = message.slice(prefixIndex + prefix.length).trim()

  if (!categoriesStr) {
    return []
  }

  return categoriesStr
    .split(',')
    .map((cat) => cat.trim())
    .filter((cat) => cat.length > 0)
}

/**
 * Get the moderation configuration for a set of flagged categories.
 * Returns the highest severity config based on priority.
 *
 * @param categories - Array of flagged category strings
 * @returns ModerationConfig with severity, crisis resources flag, and i18n key
 */
export const getModerationConfig = (categories: string[]): ModerationConfig => {
  if (categories.length === 0) {
    return getUnknownModerationConfig()
  }

  // Find highest severity and determine if crisis resources should be shown
  const result = categories.reduce(
    (acc, category) => {
      const config = CATEGORY_SEVERITY_MAP[category]

      if (config) {
        const severityIndex = SEVERITY_PRIORITY.indexOf(config.severity)

        if (severityIndex > acc.highestSeverityIndex) {
          return {
            highestSeverityIndex: severityIndex,
            matchedSeverity: config.severity,
            // Show crisis resources if any flagged category requires it
            showCrisisResources: acc.showCrisisResources || config.showCrisisResources,
          }
        }

        // Even if this isn't highest severity, still check for crisis resources
        if (config.showCrisisResources && !acc.showCrisisResources) {
          return { ...acc, showCrisisResources: true }
        }
      }

      return acc
    },
    {
      highestSeverityIndex: -1,
      showCrisisResources: false,
      matchedSeverity: 'UNKNOWN' as ModerationSeverity,
    },
  )

  // If no categories matched, return UNKNOWN
  if (result.highestSeverityIndex === -1) {
    return getUnknownModerationConfig()
  }

  return {
    severity: result.matchedSeverity,
    showCrisisResources: result.showCrisisResources,
    i18nKey: result.matchedSeverity,
  }
}

/**
 * Get the default config for unknown/unrecognized moderation errors.
 * Used when:
 * - Categories cannot be parsed from the message
 * - Categories don't match any known patterns
 * - Error comes from a different provider with unknown format
 */
export const getUnknownModerationConfig = (): ModerationConfig => ({
  severity: 'UNKNOWN',
  showCrisisResources: false,
  i18nKey: 'UNKNOWN',
})

/**
 * Check if an error code indicates a moderation error.
 * Extensible for other LLM providers.
 *
 * @param code - The error code from errorInfo
 * @returns true if this is a moderation-related error
 */
export const isModerationError = (code: string): boolean => MODERATION_ERROR_CODES.has(code)

/**
 * Process errorInfo from a subscription payload and extract moderation error if present.
 *
 * @param errorInfo - Array of error objects from GraphQL payload
 * @returns ModerationError if moderation error detected, null otherwise
 */
export const processModerationError = (
  errorInfo: Array<{ code: string; message: string }> | undefined | null,
): ModerationError | null => {
  if (!errorInfo || errorInfo.length === 0) {
    return null
  }

  const firstError = errorInfo[0]

  if (!isModerationError(firstError.code)) {
    return null
  }

  const categories = parseModerationCategories(firstError.message)
  const config = getModerationConfig(categories)

  return {
    categories,
    config,
  }
}
